import React from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const MARCH_MAX = 12;
const BALANCE_MAX = 16;
const TOTAL_MAX = MARCH_MAX + BALANCE_MAX;

const computeEtScores = (register, scale) => {
    if (!register || !scale) return null;
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    const factors = Array.isArray(scale?.factors) ? scale.factors : [];
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    if (!questions.length || !responses.length) return null;

    const questionById = new Map(questions.map(q => [q.id, q]));
    const factorMap = new Map();
    factors.forEach(f => factorMap.set(f.id, f));

    let totalScore = 0;
    let marchScore = 0;
    let balanceScore = 0;
    
    const classify = (q) => {
        const factor = q?.scaleFactorId ? factorMap.get(q.scaleFactorId) : null;
        const text = [
            q?.section,
            q?.groupName,
            q?.dimension,
            factor?.name,
            factor?.code,
            q?.code,
            q?.text,
        ].filter(Boolean).join(' ').toLowerCase();
        if (text.includes('marcha') || text.includes('gait')) return 'march';
        if (text.includes('equilibrio') || text.includes('balance')) return 'balance';
        return null;
    };

    // Scores already have fixed maximums; question data used only for grouping actual responses

    responses.forEach(resp => {
        const q = questionById.get(resp.scaleQuestionId);
        if (!q) return;
        const opt = (q.questionOptionScores || []).find(o => {
            const sid = o.scaleOptionId || o.scaleOption?.id || o.id;
            return Number(sid) === Number(resp.scaleOptionId);
        });
        
        // Priorizamos el score de la opción, luego el del registro
        const score = opt?.score !== undefined ? opt.score : resp.score;
        const finalScore = toNumberOrNull(score);
        
        if (finalScore == null) return;
        
        totalScore += finalScore;
        const groupType = classify(q); // Usamos el resultado de classify directamente
        
        if (groupType === 'march') {
            marchScore += finalScore;
        } else if (groupType === 'balance') {
            balanceScore += finalScore;
        }
    });

    let riskLevel = 'Bajo riesgo de caidas';
    if (totalScore <= 18) riskLevel = 'Alto riesgo de caidas';
    else if (totalScore <= 24) riskLevel = 'Riesgo moderado de caidas';

    return {
        totalScore,
        totalMaxScore: TOTAL_MAX,
        marchScore,
        marchMaxScore: MARCH_MAX,
        balanceScore,
        balanceMaxScore: BALANCE_MAX,
        riskLevel,
    };
};

export const ScaleResultET = ({
    title = 'Resultados',
    totalScore,
    totalMaxScore,
    marchScore,
    marchMaxScore,
    balanceScore,
    balanceMaxScore,
    riskLevel,
    interpretationItems = [
        { label: '< 19 puntos: Alto riesgo de caidas' },
        { label: '19-24 puntos: Riesgo moderado de caidas' },
        { label: '24-28 puntos: Bajo riesgo de caidas' },
    ],
    onComputedResult,
    register,
    scale,
}) => {
    
    const derived = React.useMemo(() => computeEtScores(register, scale), [register, scale]);
    
    React.useEffect(() => {
        // Usamos onComputedResult, que es lo que recibimos por props
        if (derived && typeof onComputedResult === 'function') {
            onComputedResult(derived); 
        }
    }, [derived, onComputedResult]);

    const fallbackMarchMax = MARCH_MAX;
    const fallbackBalanceMax = BALANCE_MAX;
    const fallbackTotalMax = TOTAL_MAX;

    const displayMarchScore = toNumberOrNull(marchScore) ?? derived?.marchScore ?? 0;
    const displayMarchMax = toNumberOrNull(marchMaxScore) ?? fallbackMarchMax;

    const displayBalanceScore = toNumberOrNull(balanceScore) ?? derived?.balanceScore ?? 0;
    const displayBalanceMax = toNumberOrNull(balanceMaxScore) ?? fallbackBalanceMax;

    const displayTotalScore = toNumberOrNull(totalScore) ?? derived?.totalScore ?? 0;
    const displayTotalMax = toNumberOrNull(totalMaxScore) ?? fallbackTotalMax;

    const displayRiskLevel = riskLevel || derived?.riskLevel;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16, color: '#0d1b2a' }}>
                Puntuación Total: {displayTotalScore}/{displayTotalMax}
            </Typography>

            <List style={{ paddingTop: 0, marginBottom: 16 }}>
                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                        primary={`Puntuación Marcha: ${displayMarchScore}/${displayMarchMax}`}
                    />
                </ListItem>
                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                        primary={`Puntuación Equilibrio: ${displayBalanceScore}/${displayBalanceMax}`}
                    />
                </ListItem>
                {displayRiskLevel && (
                    <ListItem disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={`Nivel de Riesgo: ${displayRiskLevel}`}
                        />
                    </ListItem>
                )}
            </List>

            <Box mb={1} ml={1}>
                <Typography variant="body2" style={{ fontStyle: 'italic', color: '#0d1b2a' }}>
                    Interpretación:
                </Typography>
            </Box>

            <List style={{ paddingTop: 0 }}>
                {interpretationItems.map((item, index) => (
                    <ListItem key={item?.label ?? index} disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon
                                fontSize="small"
                                style={{ color: item?.muted ? '#9e9e9e' : '#0d1b2a' }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{
                                style: {
                                    color: item?.muted ? '#9e9e9e' : '#0d1b2a',
                                },
                            }}
                            primary={item?.label || ''}
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default ScaleResultET;
